import {
  DACH_USER_ROLE_CHANGE,
  GET_AZURE_REDIRECT_URL_SUCCESS,
  GET_REDIRECT_URL_SUCCESS,
  SET_ACCESS_TOKEN,
  SET_URLS,
  USER_LOGIN_SUCCESS,
  USER_OPERATING_UNIT_CHANGE,
  USER_ROLE_CHANGE
} from './types'

import { Appid, Operatingunit, Roles } from '@cbgms/api'
import { combineReducers } from 'redux'
import { createReducer } from '@cbgms/base/utils/redux'
import decode from 'jwt-decode'

const defaultURLsState: { [key: string]: string } = {}

const urls = createReducer(defaultURLsState, {
  [SET_URLS](_, { payload }) {
    return payload
  }
})

const redirectURI = createReducer('', {
  [GET_REDIRECT_URL_SUCCESS](_, { payload }) {
    const url = payload.data.Data.URL as string
    const host = window.location.host

    const redirectURI = url.replace('{redirect_uri}', `https://${host}/auth/login`)

    return redirectURI
  }
})

const azureRedirectURI = createReducer('', {
  [GET_AZURE_REDIRECT_URL_SUCCESS](_, { payload }) {
    const url = payload.data.Data.URL as string
    const host = window.location.host

    const redirectURI = url.replace('{redirect_uri}', `https://${host}/auth/login`)

    return redirectURI
  }
})

export interface DefaultTokenReducerState {
  AccessToken: string
  ExpiresIn: number
  IdToken: string
  RefreshToken: string
  TokenType: string
}

const defaultTokenReducerState: DefaultTokenReducerState = {
  AccessToken: '',
  ExpiresIn: 0,
  IdToken: '',
  RefreshToken: '',
  TokenType: 'Bearer'
}

const tokens = createReducer(defaultTokenReducerState, {
  [USER_LOGIN_SUCCESS]: (_, { payload }: any) => {
    const { IDToken, ...rest } = payload.data.Data.AuthenticationResult
    return {
      ...rest,
      IdToken: IDToken
    }
  },
  [SET_ACCESS_TOKEN]: (state, { payload }) => {
    const { AccessToken, IDToken } = payload.data.Data.AuthenticationResult

    return {
      ...defaultTokenReducerState,
      ...state,
      AccessToken,
      IdToken: IDToken
    }
  }
})

export interface IUserReducerState {
  given_name: string
  family_name: string
  email: string
  'custom:role': Roles
  company_name?: string
  ou_name?: string
  AppId?: string
  roles?: Roles[]
  dach_role?: string
}

type UserReducerState = Readonly<IUserReducerState>

const defaultState: UserReducerState = {
  given_name: '',
  family_name: '',
  email: '',
  'custom:role': Roles.None,
  company_name: '',
  roles: [],
  dach_role: ''
}

interface OktaToken extends Omit<IUserReducerState, 'custom:role'> {
  'custom:role': Roles
  family_name: ''
  identities: [
    {
      providerName: string
    }
  ]
}

interface AzureToken extends Omit<IUserReducerState, 'custom:role'> {
  roles: Roles[]
  unique_name: ''
  name: ''
}

function isOktaToken(token: OktaToken | AzureToken): token is OktaToken {
  return (token as OktaToken)?.identities?.[0]?.providerName === 'Okta'
}

const user = createReducer(defaultState, {
  [USER_LOGIN_SUCCESS]: (_: any, { payload }: any) => {
    try {
      const decodedToken = decode<OktaToken | AzureToken>(payload.data.Data.AuthenticationResult.IDToken)

      if (isOktaToken(decodedToken)) {
        return {
          ...decodedToken,
          'custom:role': decodedToken['custom:role'] ?? Roles.None,
          ou_name: Operatingunit.ModelOu,
          AppId: String(Appid.ModelOuAppId),
          dach_role: decodedToken.roles?.filter(role => role.toLowerCase().startsWith('dach_'))[0]
        }
      } else {
        const rawRole = decodedToken.roles[0] ?? Roles.None
        const baseRole = rawRole.toLowerCase().split('-')[0] as Roles
        return {
          ...decodedToken,
          email: decodedToken.unique_name,
          given_name: '',
          family_name: decodedToken.name,
          'custom:role': baseRole,
          ou_name: Operatingunit.ModelOu,
          AppId: String(Appid.ModelOuAppId),
          dach_role: decodedToken.roles?.filter(role => role.toLowerCase().startsWith('dach_'))[0]
        }
      }
    } catch (error) {
      console.error('Error decoding token:', error)
      return defaultState
    }
  },

  [USER_ROLE_CHANGE]: (state: any, { payload }: any) => ({
    ...state,
    'custom:role': payload.role
  }),
  [USER_OPERATING_UNIT_CHANGE]: (state: any, { payload }: any) => ({
    ...state,
    ou_name: payload.operatingunit,
    AppId: payload.appId
  }),
  [DACH_USER_ROLE_CHANGE]: (state: any, { payload }: any) => ({
    ...state,
    dach_role: payload.dach_role
  })
})

const authenticated = createReducer(false, {
  [USER_LOGIN_SUCCESS]() {
    return true
  }
})

export default combineReducers({
  authenticated,
  redirectURI,
  azureRedirectURI,
  urls,
  user,
  tokens
})
